/**
 * @file     scrollStepIntoView.ts
 * @purpose  Scrolls a flow step container into view on step transitions. Prevents forms from scrolling out of viewport on step change or autofill.
 */

/**
 * Scroll an element into view with a top offset for the sticky navbar.
 * Uses smooth scrolling and a small delay to let the DOM settle after step transitions.
 */
export function scrollStepIntoView(element: HTMLElement | null, delay = 120) {
  if (!element) return;
  setTimeout(() => {
    const navbarHeight = 80;

    // The app scrolls inside an inner container ([data-app-scroll-root]), not the
    // window. Target that scroller; fall back to the document element only if the
    // step is somehow rendered outside the app frame.
    const scroller =
      (element.closest('[data-app-scroll-root]') as HTMLElement | null) ??
      document.scrollingElement ??
      document.documentElement;

    const scrollerRect = scroller.getBoundingClientRect();
    const rect = element.getBoundingClientRect();
    // Position of the element within the scroller's current scroll space.
    const relativeTop = rect.top - scrollerRect.top;
    const targetTop = scroller.scrollTop + relativeTop - navbarHeight - 8;

    // Only scroll if the step isn't already comfortably in view near the top.
    const isVisibleNearTop =
      relativeTop >= navbarHeight && relativeTop <= scroller.clientHeight * 0.5;
    if (isVisibleNearTop) return;

    scroller.scrollTo({ top: Math.max(targetTop, 0), behavior: 'smooth' });
  }, delay);
}
